import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { useBulkSetCopilotBudgetMutation } from "state/rtkQuery/user/userMutations";
import { useCopilotBudgetCapabilities, useCopilotUsageCapabilities } from "components/RequirePermission/useCapabilities";
import { showToast } from "utils/reusableToast";

const ACCENT = "#26890d";
const USAGE_RED = "#E2231A";
const WARNING_ACCENT = "#d32f2f";
const WARNING_TEXT = "#8e0000";
const WARNING_BG = "#fdecea";

const formatUsd = (v) =>
  "$" + Number(v ?? 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// GitHub Copilot budgets come in three scopes — a per-user ULB, a
// cost-center budget shared by everyone in that cost center, or the
// universal (enterprise-wide) budget. Same labeling/coloring as the admin
// "AIC Budget Requests" table, so org owners see one consistent vocabulary
// for "which budget is currently effective" everywhere in the app.
const SCOPE_LABELS = {
  user: "ULB",
  multi_user_cost_center: "Cost Center Level Budget",
  multi_user_customer: "Universal Budget",
};

const scopeAppliedLabel = (scope) =>
  scope ? `${SCOPE_LABELS[scope] ?? "Shared Budget"} Applied` : null;

const SCOPE_CHIP_STYLES = {
  user: { color: ACCENT, bg: "rgba(38,137,13,0.1)" },
  multi_user_cost_center: { color: "#0b5fa5", bg: "rgba(11,95,165,0.1)" },
  multi_user_customer: { color: "#6a4fb3", bg: "rgba(106,79,179,0.1)" },
};

const BudgetScopeChip = ({ scope }) => {
  if (!scope) return null;
  const { color, bg } = SCOPE_CHIP_STYLES[scope] ?? { color: "#666", bg: "rgba(0,0,0,0.06)" };
  return (
    <Chip
      label={scopeAppliedLabel(scope)}
      size="small"
      sx={{ color, backgroundColor: bg, fontWeight: 700, fontSize: "0.62rem", height: "18px" }}
    />
  );
};

// One row per selected user — budgets come straight from `user.copilotBudget`
// (already returned by the joined-users list this modal's selection is built
// from, no need to refetch per-user) so we can detect whether the requested
// amount would make the backend delete this user's ULB instead of
// creating/patching it. Cost center is a hard floor: a ULB set at or below it
// is redundant, since the user would inherit the cost-center amount anyway.
// Universal is not a floor, but an exact match to it is equally redundant.
// Either way the backend deletes the ULB and the user's effective budget
// reverts to whichever shared budget it inherits — NOT the amount the admin
// typed — so we warn with the resulting amount up front.
const SelectedUserRow = ({ user, newAmount, isLast, onDeleteWarningChange, onRemove, canViewUsage }) => {
  const budgetData = user.copilotBudget;
  const budgets = budgetData?.budgets ?? [];
  // Only ai_credits-SKU budgets participate in the floor/redundancy rules
  // below — a non-AI-credits entry sharing the same scope must not be
  // mistaken for the user's ULB or their cost-center/universal baseline.
  const aiCreditsBudgets = budgets.filter((b) => b.budget_product_sku === "ai_credits");
  const hasUlb = aiCreditsBudgets.some((b) => b.budget_scope === "user");
  const costCenterBudget = aiCreditsBudgets.find((b) => b.budget_scope === "multi_user_cost_center");
  const universalBudget = aiCreditsBudgets.find((b) => b.budget_scope === "multi_user_customer");

  // `effective_budget` only carries id/amount/consumed — match it back
  // against the full `budgets[]` (which has the full scope info, and may
  // legitimately be a non-ai_credits entry) to know which of the user's
  // budgets is actually applied to them right now.
  const effectiveScope = budgets.find((b) => b.id === budgetData?.effective_budget?.id)?.budget_scope;

  let willDeleteUlb = false;
  let resultingBudgetAmount = null;
  let resultingBudgetLabel = null;
  if (hasUlb && newAmount != null) {
    if (costCenterBudget) {
      if (newAmount <= costCenterBudget.budget_amount) {
        willDeleteUlb = true;
        resultingBudgetAmount = costCenterBudget.budget_amount;
        resultingBudgetLabel = "cost center budget";
      }
    } else if (universalBudget && newAmount === universalBudget.budget_amount) {
      willDeleteUlb = true;
      resultingBudgetAmount = universalBudget.budget_amount;
      resultingBudgetLabel = "universal budget";
    }
  }

  useEffect(() => {
    onDeleteWarningChange(user.email, willDeleteUlb ? { amount: resultingBudgetAmount, label: resultingBudgetLabel } : null);
    return () => onDeleteWarningChange(user.email, null);
  }, [user.email, willDeleteUlb, resultingBudgetAmount, resultingBudgetLabel, onDeleteWarningChange]);

  return (
    <Box
      sx={{
        px: 2,
        py: 1.3,
        borderBottom: isLast ? "none" : "1px solid #f0f0f0",
        backgroundColor: willDeleteUlb ? WARNING_BG : "transparent",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {willDeleteUlb && <WarningAmberIcon sx={{ fontSize: "1rem", color: WARNING_ACCENT, flexShrink: 0 }} />}
            <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#1a1a1a" }}>
              {user.name}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "0.8rem", color: "#999" }}>{user.email}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          <Box sx={{ textAlign: "right" }}>
            {!canViewUsage ? (
              <Typography sx={{ fontSize: "0.75rem", color: "#999" }}>Usage hidden</Typography>
            ) : budgetData?.effective_budget ? (
              <>
                <Typography sx={{ fontSize: "0.75rem", color: "#999" }}>
                  Used {formatUsd(budgetData.effective_budget.consumed_amount)} of{" "}
                  {formatUsd(budgetData.effective_budget.budget_amount)}
                </Typography>
                <Box sx={{ mt: 0.3 }}>
                  <BudgetScopeChip scope={effectiveScope} />
                </Box>
              </>
            ) : (
              <Typography sx={{ fontSize: "0.75rem", color: "#999" }}>No budget on record</Typography>
            )}
          </Box>
          <Tooltip title="Remove from selection">
            <IconButton
              size="small"
              onClick={() => onRemove(user.id)}
              sx={{ p: 0.25, color: "#999", "&:hover": { color: USAGE_RED } }}
            >
              <HighlightOffIcon sx={{ fontSize: "1.1rem" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {willDeleteUlb && (
        <Typography sx={{ fontSize: "0.76rem", color: WARNING_TEXT, fontWeight: 600, mt: 0.5 }}>
          Will delete this user's individual override → effective budget becomes {formatUsd(resultingBudgetAmount)}{" "}
          ({resultingBudgetLabel}), not the {formatUsd(newAmount)} entered.
        </Typography>
      )}
    </Box>
  );
};

// Admin direct-set of AI Credit budget for the selected users — bypasses the
// increase/decrease request flow entirely and applies immediately, no
// approval needed.
const BulkChangeBudgetModal = ({ show, close, selectedUsers = [], platformId, approvedBy, onRemoveUser }) => {
  const { orgId } = useParams();

  // Viewing a selected user's usage (Used $X of $Y + scope chip) is a
  // separate capability from setting their budget — someone who can only
  // override budgets (user:copilot:budget:create, already gating this modal)
  // but lacks the usage-read bundle doesn't see usage figures here either.
  // useCopilotUsageCapabilities is already scoped to THIS org's role (see
  // useEffectiveOrgPermission) — a role held on a DIFFERENT org can't leak in.
  const {
    canViewUsage: canViewUsageBase,
    canViewUsageById,
    canViewModelUsage,
    canViewUsageByCostCenter,
    canViewUsageByCostCenterId,
  } = useCopilotUsageCapabilities(orgId);
  const canViewUsage =
    canViewUsageBase && canViewUsageById && canViewModelUsage && canViewUsageByCostCenter && canViewUsageByCostCenterId;

  // Confirm directly overrides each selected user's AI Credit budget — the
  // endpoint upserts a ULB row (create if the user has none yet, update if
  // they already do), so both permissions are required unconditionally, same
  // as the trigger button's own gate in ReusableDataGrid.js. Re-checked here
  // (not just at the trigger) so this modal is safe to gate on its own if it
  // is ever opened from somewhere else in the future.
  const { canCreateBudget, canUpdateBudget } = useCopilotBudgetCapabilities(orgId);
  const canChangeBudget = canCreateBudget && canUpdateBudget;

  const [budgetAmount, setBudgetAmount] = useState("");
  const [deleteWarnings, setDeleteWarnings] = useState({});
  const [deleteWarningAcked, setDeleteWarningAcked] = useState(false);

  const [bulkSetCopilotBudget, { isLoading: submitting }] = useBulkSetCopilotBudgetMutation();

  const parsedAmount = Number(budgetAmount);
  const amountIsValid = budgetAmount !== "" && !Number.isNaN(parsedAmount) && parsedAmount >= 0;

  const handleDeleteWarningChange = (email, warning) => {
    setDeleteWarnings((prev) => {
      const prevWarning = prev[email] ?? null;
      const sameAmount = prevWarning?.amount === warning?.amount && prevWarning?.label === warning?.label;
      if ((prevWarning == null) === (warning == null) && sameAmount) return prev;
      const next = { ...prev };
      if (warning == null) delete next[email];
      else next[email] = warning;
      return next;
    });
  };

  const affectedEmails = Object.keys(deleteWarnings);
  const affectedCount = affectedEmails.length;

  // Nothing is ever "blocked" — a delete is a valid, intended outcome once
  // the requested amount is at/below a user's cost-center floor (or exactly
  // matches their universal budget). We only require the admin to
  // acknowledge it, since the resulting effective budget differs from what
  // they typed.
  const canSubmit =
    canChangeBudget &&
    amountIsValid &&
    selectedUsers.length > 0 &&
    !submitting &&
    (affectedCount === 0 || deleteWarningAcked);

  const resetAndClose = () => {
    setBudgetAmount("");
    setDeleteWarnings({});
    setDeleteWarningAcked(false);
    close();
  };

  // Nothing left to act on once the last user is removed from the selection.
  useEffect(() => {
    if (show && selectedUsers.length === 0) {
      setBudgetAmount("");
      setDeleteWarnings({});
      setDeleteWarningAcked(false);
      close();
    }
  }, [selectedUsers.length, show, close]);

  const handleAmountChange = (e) => {
    setBudgetAmount(e.target.value);
    setDeleteWarningAcked(false);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      const result = await bulkSetCopilotBudget({
        platformId,
        orgId,
        emails: selectedUsers.map((user) => user.email),
        budgetAmount: parsedAmount,
        approvedBy,
        preventFurtherUsage: true,
        willAlert: false,
      }).unwrap();

      const succeededCount = result?.succeeded_count ?? selectedUsers.length;
      const failedCount = result?.failed_count ?? 0;

      showToast({
        title: failedCount > 0 ? "Budget update partially completed" : "Budget updated",
        description:
          result?.message ??
          (failedCount > 0
            ? `${succeededCount} of ${selectedUsers.length} budgets set successfully, ${failedCount} failed: ${result.failed
                .map((f) => `${f.email} (${f.error})`)
                .join("; ")}`
            : `Set ${formatUsd(parsedAmount)} as the budget for ${succeededCount} user${succeededCount === 1 ? "" : "s"}.`),
        type: failedCount > 0 ? "warning" : "success",
        id: "bulk-budget-toast",
      });
      resetAndClose();
    } catch (error) {
      showToast({
        title: "Budget update failed",
        description: "We couldn't update the budget for the selected users. Please try again.",
        type: "error",
        id: "bulk-budget-error-toast",
      });
    }
  };

  return (
    <Dialog
      open={show}
      onClose={submitting ? undefined : resetAndClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `3px solid ${ACCENT}`,
          py: 2,
          px: 3,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a1a" }}>
          Change Budget
        </Typography>
        <IconButton size="small" onClick={resetAndClose} disabled={submitting} sx={{ color: "#555" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2.5, "&.MuiDialogContent-root": { pt: 2.5 } }}>
        <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#333", mb: 0.7 }}>
          {selectedUsers.length} user{selectedUsers.length === 1 ? "" : "s"} selected
        </Typography>
        <Box
          sx={{
            maxHeight: "18rem",
            overflowY: "auto",
            border: "1px solid #e8e8e8",
            borderRadius: "8px",
            mb: 1,
          }}
        >
          {selectedUsers.map((user, index) => (
            <SelectedUserRow
              key={user.id ?? user.email ?? index}
              user={user}
              newAmount={amountIsValid ? parsedAmount : null}
              isLast={index === selectedUsers.length - 1}
              onDeleteWarningChange={handleDeleteWarningChange}
              onRemove={onRemoveUser}
              canViewUsage={canViewUsage}
            />
          ))}
        </Box>

        {affectedCount > 0 && (
          <Box
            sx={{
              backgroundColor: WARNING_BG,
              border: `1px solid ${WARNING_ACCENT}`,
              borderRadius: "8px",
              px: 1.5,
              py: 1,
              mb: 1.5,
            }}
          >
            <Typography sx={{ fontSize: "0.76rem", color: WARNING_TEXT, fontWeight: 600, mb: 0.7 }}>
              {formatUsd(parsedAmount)} is at or below {affectedCount === 1 ? "the" : "a"} selected user
              {affectedCount === 1 ? "'s" : "s'"} cost center budget (or matches their universal budget), so{" "}
              {affectedCount === 1 ? "that user's" : `${affectedCount} users'`} individual override will be deleted
              instead of updated — see the highlighted row{affectedCount === 1 ? "" : "s"} above for what each will
              end up with.
            </Typography>
            <FormControlLabel
              sx={{ ml: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={deleteWarningAcked}
                  onChange={(e) => setDeleteWarningAcked(e.target.checked)}
                  sx={{ p: 0.5, color: WARNING_ACCENT, "&.Mui-checked": { color: WARNING_ACCENT } }}
                />
              }
              label={
                <Typography sx={{ fontSize: "0.74rem", color: "#333" }}>
                  I understand the affected user{affectedCount === 1 ? "'s" : "s'"} individual override will be
                  deleted
                </Typography>
              }
            />
          </Box>
        )}

        <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#333", mb: 0.7, mt: affectedCount > 0 ? 0 : 1.5 }}>
          New Budget Amount (USD) <span style={{ color: USAGE_RED }}>*</span>
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="e.g. 150"
          value={budgetAmount}
          onChange={handleAmountChange}
          error={budgetAmount !== "" && !amountIsValid}
          helperText={
            budgetAmount !== "" && !amountIsValid
              ? "Enter a valid, non-negative amount"
              : "This sets each selected user's budget to this exact amount, replacing whatever they currently have."
          }
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          sx={{
            mb: 1.5,
            "& .MuiInputBase-input": { fontSize: "0.82rem" },
            "& .MuiFormHelperText-root": { fontSize: "0.68rem", mx: 0 },
          }}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid #f0f0f0",
          justifyContent: "flex-end !important",
        }}
      >
        <Button onClick={resetAndClose} disabled={submitting} sx={{ textTransform: "none", color: "#555" }}>
          Cancel
        </Button>
        <Tooltip
          title={
            !canChangeBudget
              ? "Requires the user:copilot:budget:create and user:copilot:budget:id:update permissions"
              : affectedCount > 0 && !deleteWarningAcked
                ? "Confirm you understand the affected users' individual overrides will be deleted before continuing."
                : ""
          }
        >
          <span>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!canSubmit}
              sx={{
                textTransform: "none",
                backgroundColor: ACCENT,
                "&:hover": { backgroundColor: "#1e6b0a" },
              }}
            >
              {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Confirm"}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default BulkChangeBudgetModal;
